import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Star, ImagePlus, Loader2, PenLine, X } from "lucide-react";
import { MENU, TAB_ORDER } from "@/data/menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ITEM_NAMES = TAB_ORDER.flatMap((k) => MENU[k].items.map((i) => i.name));

const fileToDataUrl = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const max = 700;
        const scale = Math.min(1, max / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.7));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });

export default function ReviewDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState(ITEM_NAMES[0]);
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [photo, setPhoto] = useState("");
  const [loading, setLoading] = useState(false);

  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (f) setPhoto(await fileToDataUrl(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !comment) {
      toast.error("Please add your name and a comment");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/reviews`, { item_name: item, rating, name, comment, photo });
      toast.success("Thanks for your review!");
      onCreated?.(res.data);
      setOpen(false);
      setName(""); setComment(""); setPhoto(""); setRating(5);
    } catch {
      toast.error("Could not submit review. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="write-review-btn" className="inline-flex items-center gap-2 rounded-full bg-[#DE9A2C] hover:bg-[#c9861f] text-[#17120C] font-bold px-6 py-3 transition-colors">
          <PenLine size={18} /> Write a Review
        </button>
      </DialogTrigger>
      <DialogContent className="bg-beige border border-line max-w-lg" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-bold text-coffee">Share your experience</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-coffee-muted font-bold mb-1.5">Item</label>
            <select data-testid="review-item" value={item} onChange={(e) => setItem(e.target.value)}
              className="w-full rounded-lg border border-line bg-white px-3 py-2.5 text-coffee outline-none focus:border-teal-brand">
              {ITEM_NAMES.map((n) => <option key={n}>{n}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-coffee-muted font-bold mb-1.5">Rating</label>
            <div className="flex gap-1" data-testid="review-stars">
              {[1, 2, 3, 4, 5].map((s) => (
                <button type="button" key={s} data-testid={`review-star-${s}`} onClick={() => setRating(s)} onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(0)}>
                  <Star size={28} className={s <= (hover || rating) ? "fill-[#DE9A2C] text-[#DE9A2C]" : "text-line"} />
                </button>
              ))}
            </div>
          </div>

          <input data-testid="review-name" placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border border-line bg-white px-3 py-2.5 text-coffee outline-none focus:border-teal-brand" />
          <textarea data-testid="review-comment" placeholder="What did you think?" rows={3} value={comment} onChange={(e) => setComment(e.target.value)}
            className="w-full rounded-lg border border-line bg-white px-3 py-2.5 text-coffee outline-none focus:border-teal-brand resize-none" />

          <div>
            <label data-testid="review-photo-label" className="inline-flex items-center gap-2 cursor-pointer rounded-full border border-line px-4 py-2 text-sm font-medium text-coffee-soft hover:border-teal-brand transition-colors">
              <ImagePlus size={16} /> {photo ? "Change photo" : "Add a photo"}
              <input data-testid="review-photo" type="file" accept="image/*" onChange={onPhoto} className="hidden" />
            </label>
            {photo && (
              <div className="mt-3 relative inline-block">
                <img src={photo} alt="preview" className="h-24 w-24 rounded-lg object-cover border border-line" />
                <button type="button" onClick={() => setPhoto("")} className="absolute -top-2 -right-2 grid place-items-center w-6 h-6 rounded-full bg-[#8A2231] text-white"><X size={13} /></button>
              </div>
            )}
          </div>

          <button data-testid="review-submit" type="submit" disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#8A2231] hover:bg-[#6E1926] disabled:opacity-60 text-white font-semibold py-3 transition-colors">
            {loading ? <Loader2 size={18} className="animate-spin" /> : null} {loading ? "Submitting…" : "Submit Review"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
